import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import useStyles from 'isomorphic-style-loader/useStyles';

import { DnDProviderHOC } from '../../../private/LearnEngineComponents/Common/DnDProviderHOC/DnDProviderHOC';
import { DnDOptionsV2 } from '../../../private/LearnEngineComponents/DragAndDropAnswerV2/DnDOptionsV2/DnDOptionsV2';
import {
	AnswersType,
	DnDAnswerOption,
	OptionDropBackType,
} from '../../../private/LearnEngineComponents/DragAndDropAnswerV2/interface';
import { ISlAnswerProps } from '../../globalInterfaces/learnEngineInterfaces';
import {
	calculateMaxAnswersLength,
	getAreaId,
} from '../../../private/LearnEngineComponents/DragAndDropAnswerV2/utils';
import { DnDQuestionV2 } from '../../../private/LearnEngineComponents/DragAndDropAnswerV2/DnDQuestionV2/DnDQuestionV2';

import s from './SlDragAndDropAnswerV2.scss';
import { SlRichText } from '../SlRichText/SlRichText';
import { AnswerOptionStatus, IUIType } from '../../../../api/private/learn-engine-api/learn-engine-api.interface';

export function SlDragAndDropAnswerV2({
	questionParts,
	options,
	onChange,
	isValid,
	uiType,
	instructions,
}: ISlAnswerProps): JSX.Element {
	useStyles(s);
	const [selectedAnswers, setSelectedAnswers] = React.useState<AnswersType>(
		{},
	);

	const [answerOptions, setAnswerOptions] = useState<DnDAnswerOption[]>(options);

	const maxAnswersLength: number = useMemo(
		() => calculateMaxAnswersLength(questionParts),
		[questionParts],
	);

	const correctAnswers = useMemo(() => {
		const result: { [key: string]: AnswerOptionStatus } = {};

		options.forEach((option) => {
			Object.keys(selectedAnswers).forEach((key) => {
				if (selectedAnswers[key].id === option.id) {
					result[key] = option.status;
				}
			});
		});
		return result;
	}, [options, selectedAnswers]);

	useEffect(() => {
		const answers: AnswersType = options
			.filter((o) => o.status && o.status !== AnswerOptionStatus.Initial)
			.sort((o1, o2) => o1.orderNumber - o2.orderNumber)
			.reduce((obj, curr) => ({
				...obj,
				[curr.orderNumber - 1]: curr,
			}), {});
		setSelectedAnswers(answers);
		setAnswerOptions(options);
		isValid(
			options.filter(
				(o) => o.status && o.status !== AnswerOptionStatus.Initial,
			).length === maxAnswersLength,
		);
	}, [options]);

	const onOptionSelectHandler = (option: DnDAnswerOption): void => {
		const areaId = getAreaId(selectedAnswers);
		if (areaId >= maxAnswersLength) {
			return;
		}
		const newOptions = options.map((o) => {
			if (o.id === option.id) {
				return {
					...o,
					status: AnswerOptionStatus.Selected,
					orderNumber: areaId + 1,
				};
			}
			return o;
		});
		onChange(newOptions);
	};

	const onOptionDropBackHandler = ({ value }: OptionDropBackType): void => {
		const newOptions = options.map((o) => {
			if (o.id === value.id && o.status === AnswerOptionStatus.Selected) {
				return {
					...o,
					status: AnswerOptionStatus.Initial,
				};
			}
			return o;
		});
		onChange(newOptions);
	};

	const onQuestionSelectHandler = (id: number): void => {
		if (selectedAnswers[id]) {
			const newOptions = options.map((o) => {
				if (
					o.id === selectedAnswers[id].id
                    && o.status === AnswerOptionStatus.Selected
				) {
					return {
						...o,
						status: AnswerOptionStatus.Initial,
					};
				}
				return o;
			});
			onChange(newOptions);
		}

		const questionItems: HTMLCollectionOf<Element> = document.getElementsByClassName(
            	'sl-dnd-question-v2__item-container',
		);
		if (questionItems.length) {
			questionItems[0].scrollIntoView({
				block: 'start',
				behavior: 'smooth',
			});
		}
	};

	const footerPortal = document.getElementById('lessonPageOptionsPortal');

	return (
		<DnDProviderHOC>
			<div className="sl-dnd-v2">
				{instructions && uiType === IUIType.Code ? (
					<div className="sl-dnd-v2__instructions">
						<SlRichText content={instructions} />
					</div>
				) : null}
				<DnDQuestionV2
					isCode={uiType === IUIType.Code}
					content={questionParts}
					selectedAnswers={selectedAnswers}
					correctAnswers={correctAnswers}
					onOptionSelect={onQuestionSelectHandler}
				/>
				{footerPortal
                    && createPortal(
	<DnDOptionsV2
	options={answerOptions}
	onOptionSelect={onOptionSelectHandler}
	onOptionDropBack={onOptionDropBackHandler}
                    	/>,
                    	footerPortal,
                    )}
			</div>
		</DnDProviderHOC>
	);
}
